import { Link, usePage } from '@inertiajs/react';
import { ReactNode } from 'react';
import { PublicLayout } from '@/components/PublicLayout';


interface BlogLayoutProps {
  children: ReactNode;
  leftSidebar?: ReactNode;
  rightSidebar?: ReactNode;
}

interface PageProps {
  [key: string]: any;
  categories?: Array<{
    id: number;
    name: string;
    slug: string;
    postCount: number;
  }>;
  tags?: Array<{
    id: number;
    name: string;
    slug: string;
    postCount: number;
  }>;
  auth?: {
    user?: {
      id: number;
      email: string;
      name?: string;
      username?: string;
      role?: 'user' | 'admin' | 'manager';
    };
  };
}

export function BlogLayout({ children, leftSidebar, rightSidebar }: BlogLayoutProps) {
  const { categories = [], tags = [], auth } = usePage<PageProps>().props;

  // Content for the sidebar that will be shown alongside blog posts
  const SidebarContent = () => (
    <>
      {/* Categories */}
      <div className="bg-card rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4">Categories</h3>
        {categories.length > 0 ? (
          <ul className="space-y-2">
            {categories.slice(0, 6).map((category) => (
              <li key={category.id}>
                <Link
                  href={`/blog/category/${category.slug}`}
                  className="flex items-center justify-between text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  <span>{category.name}</span>
                  <span className="text-xs text-muted-foreground/70">({category.postCount})</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No categories</p>
        )}
      </div>
      
      {/* Tag Cloud */}
      <div className="bg-card rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4">Tags</h3>
        {tags.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {tags.slice(0, 15).map((tag) => (
              <Link
                key={tag.id}
                href={`/blog/tag/${tag.slug}`}
                className="text-sm bg-muted px-3 py-1 rounded-full hover:bg-primary/10 hover:text-primary transition-colors"
              >
                #{tag.name}
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No tags</p>
        )}
      </div>
      
      {/* RSS Feed Link */}
      <div className="bg-card rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4">Subscribe</h3>
        <Link
          href="/blog/feed"
          className="flex items-center text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
            <path d="M6.18 15.64a2.18 2.18 0 0 1 2.18 2.18C8.36 19 7.38 20 6.18 20C5 20 4 19 4 17.82a2.18 2.18 0 0 1 2.18-2.18M4 4.44A15.56 15.56 0 0 1 19.56 20h-2.83A12.73 12.73 0 0 0 4 7.27V4.44m0 5.66a9.9 9.9 0 0 1 9.9 9.9h-2.83A7.07 7.07 0 0 0 4 12.93V10.1z"/>
          </svg>
          RSS Feed
        </Link>
      </div>
    </>
  );

    // Three-column layout: left sidebar (TOC), main content, right sidebar
    const ContentWithSidebar = () => (
      <div className="flex flex-col lg:flex-row gap-6 min-h-[calc(100vh-20px)] relative">
         {/* Left Sidebar - TOC (sticky on both mobile and desktop) */}
         {leftSidebar && (
           <aside className="lg:w-64 shrink-0 sticky top-20 lg:top-24 z-20 order-1">
             {leftSidebar}
           </aside>
         )}
        
        {/* Main content area */}
        <div className="flex-1 min-w-0 order-2">
          {children}
        </div>
        
        {/* Right Sidebar - Categories, Tags, RSS */}
        <aside className="w-full lg:w-72 space-y-6 order-3">
          {rightSidebar || <SidebarContent />}
        </aside>
      </div>
    );

  return (
    <>

      <PublicLayout user={auth?.user}>
        {/* Main content with sidebar */}
        <div className="w-screen max-w-full px-4 md:px-8 py-8 pb-42">
          <ContentWithSidebar />
        </div>
      </PublicLayout>
    </>
  );
}

export default BlogLayout;
